import { Link } from '@tanstack/react-router';
import { useDraggable } from '@dnd-kit/core';
import { EllipsisVertical, GripVertical } from 'lucide-react';
import type { OrcamentoResponse } from '@triade/contracts';
import { ProfitBadge } from '@/components/profit-badge';
import { ROTULO_STATUS } from '@/components/orcamento/tipos';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatarData, formatarDecimal, formatarIdade, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AlertaCusto } from './alerta-custo';
import { SeloOrigem, SeloVencido } from './selo-vencido';
import { TRANSICOES, type StatusOrcamento } from './tipos';

export interface CartaoKanbanProps {
  orcamento: OrcamentoResponse;
  nomeTabela: (id: string | null) => string | undefined;
  nomeCondicao: (id: string) => string | undefined;
  /** Pede a transicao (abre a confirmacao). Menu e arrastar chegam ao mesmo lugar. */
  onMover: (o: OrcamentoResponse, para: StatusOrcamento) => void;
  /** Sem orcamento.editar nao ha arrastar nem menu "Mover para…" (a API tambem recusa). Padrao: pode. */
  podeMover?: boolean;
  agora?: Date;
}

/** Corpo do cartao, sem comportamento de arrastar (usado tambem no DragOverlay). */
export function ConteudoCartao({ orcamento: o, nomeTabela, nomeCondicao, agora, acoes }: Pick<CartaoKanbanProps, 'orcamento' | 'nomeTabela' | 'nomeCondicao' | 'agora'> & { acoes?: React.ReactNode }) {
  const e = o.exibicao;
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <Link to="/vender/orcamentos/$id" params={{ id: o.id }} className="tabular font-semibold text-ink no-underline hover:underline">
            #{o.numeroExibido}
          </Link>
          {o.vencido && <SeloVencido />}
          <SeloOrigem origem={o.origem} />
          <AlertaCusto orcamento={o} />
        </div>
        <div className="flex items-center gap-1">
          {e.lucroExibido == null && !e.faixa ? (
            <span className="text-caption text-ink-disabled">—</span>
          ) : (
            <ProfitBadge lucro={e.lucroExibido ?? undefined} faixa={e.faixa ?? undefined} meta={e.metaLucro} minimo={e.minimoLucro ?? '0'} compacto className="tabular" />
          )}
          {acoes}
        </div>
      </div>
      <div className="font-medium">{o.cliente?.nome ?? <span className="font-normal text-ink-disabled">Sem cliente</span>}</div>
      <div className="text-caption text-ink-secondary">
        {nomeTabela(o.tabelaId) ?? '—'} · {nomeCondicao(o.condicaoId) ?? '—'} · {formatarDecimal(e.quantidadeTotal, 0)} un.
      </div>
      <div className="flex items-center justify-between border-t border-line-soft pt-2">
        <span className="tabular font-semibold">{formatarMoeda(e.receitaTotal)}</span>
        <span className="tabular text-caption text-ink-muted" title={`Data ${formatarData(o.data)}`} data-testid="idade-cartao">
          {formatarIdade(o.createdAt, agora)}
        </span>
      </div>
    </>
  );
}

const CARTAO = 'flex flex-col gap-2 rounded-xl border border-border bg-card p-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]';

/** Cartao do kanban: arrasta pela alca (mouse/teclado) ou muda de coluna pelo menu "Mover para…". */
export function CartaoKanban({ orcamento: o, nomeTabela, nomeCondicao, onMover, podeMover = true, agora }: CartaoKanbanProps) {
  const destinos = podeMover ? TRANSICOES[o.status] : [];
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: o.id, data: { orcamento: o }, disabled: destinos.length === 0 });
  return (
    <article
      ref={setNodeRef}
      data-testid="cartao-orcamento"
      data-numero={o.numero}
      data-status={o.status}
      aria-label={`Orçamento ${o.numero}`}
      className={cn(CARTAO, isDragging && 'opacity-40')}
    >
      <ConteudoCartao
        orcamento={o}
        nomeTabela={nomeTabela}
        nomeCondicao={nomeCondicao}
        agora={agora}
        acoes={
          destinos.length > 0 && (
            <>
              <button
                type="button"
                aria-label={`Arrastar orçamento ${o.numero}`}
                title="Arraste, ou use o teclado (Espaço, setas, Espaço); alternativa: menu Mover para…"
                className="inline-flex size-7 cursor-grab touch-none items-center justify-center rounded-md text-ink-muted hover:bg-line-soft"
                {...listeners}
                {...attributes}
              >
                <GripVertical aria-hidden="true" className="size-4" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Mover orçamento ${o.numero}`}
                    className="inline-flex size-7 items-center justify-center rounded-md text-ink-muted hover:bg-line-soft"
                  >
                    <EllipsisVertical aria-hidden="true" className="size-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Mover para…</DropdownMenuLabel>
                  {destinos.map((d) => (
                    <DropdownMenuItem key={d} onSelect={() => onMover(o, d)}>
                      {ROTULO_STATUS[d]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )
        }
      />
    </article>
  );
}

/** Cartao flutuante que acompanha o ponteiro durante o arrasto. */
export function CartaoFlutuante(p: Pick<CartaoKanbanProps, 'orcamento' | 'nomeTabela' | 'nomeCondicao'>) {
  return (
    <div className={cn(CARTAO, 'w-[250px] cursor-grabbing shadow-float')}>
      <ConteudoCartao {...p} />
    </div>
  );
}
